import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import InvoiceDetailModal from "@/pages/admin/invoices/components/InvoiceDetailModal";
import { loadInvoices } from "@/lib/salesApi";
import { formatDateTime, formatMoney } from "@/lib/format";
import { STATUS_LABELS, STATUS_STYLES } from "@/lib/invoiceTemplate";
import type { InvoiceListItem } from "@/types";

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await loadInvoices();
    setInvoices(result.invoices);
    setError(result.error);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return invoices.filter((invoice) => {
      if (statusFilter !== "all" && invoice.status !== statusFilter) return false;
      if (!term) return true;
      return (
        (invoice.number ?? "").toLowerCase().includes(term) ||
        (invoice.ncf ?? "").toLowerCase().includes(term) ||
        (invoice.customer_name ?? "").toLowerCase().includes(term) ||
        (invoice.cashier_name ?? "").toLowerCase().includes(term)
      );
    });
  }, [invoices, search, statusFilter]);

  const stats = useMemo(() => {
    const paid = invoices.filter((i) => i.status === "paid");
    const totalPaid = paid.reduce((sum, i) => sum + Number(i.total ?? 0), 0);
    const voided = invoices.filter((i) => i.status === "void").length;
    const refunded = invoices.filter((i) => i.status === "refunded").length;
    return { count: invoices.length, totalPaid, paidCount: paid.length, voided, refunded };
  }, [invoices]);

  const statCards = [
    { key: "total", label: "Facturado (pagadas)", value: formatMoney(stats.totalPaid), icon: "ri-money-dollar-circle-line", tone: "primary" },
    { key: "paid", label: "Facturas pagadas", value: stats.paidCount, icon: "ri-checkbox-circle-line", tone: "accent" },
    { key: "void", label: "Anuladas", value: stats.voided, icon: "ri-forbid-2-line", tone: "secondary" },
    { key: "refunded", label: "Reembolsadas", value: stats.refunded, icon: "ri-arrow-go-back-line", tone: "secondary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-200 text-secondary-900",
  };

  const statusLabel = (status: string | null) =>
    status ? STATUS_LABELS[status] ?? status : "-";
  const statusStyle = (status: string | null) =>
    status ? STATUS_STYLES[status] ?? "bg-background-200 text-foreground-600" : "bg-background-200 text-foreground-600";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Facturación</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Facturas con numeración por sucursal, estados y plantillas POS 58/80mm y A4/PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/pos"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-store-2-line text-base"></i>
            Ir al POS
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-base`}></i>
            </span>
            <div className="min-w-0">
              <p className="font-heading truncate text-lg font-bold leading-none text-foreground-950">
                {card.value}
              </p>
              <p className="mt-1 truncate text-[11px] text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por número, NCF, cliente o cajero..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="all">Todos los estados</option>
          <option value="paid">Pagadas</option>
          <option value="void">Anuladas</option>
          <option value="refunded">Reembolsadas</option>
        </select>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando facturas...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-receipt-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin facturas</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Las ventas realizadas desde el Punto de Venta aparecerán aquí con su numeración y estado.
          </p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-background-200 lg:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-background-200/60 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3">Número</th>
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Cajero</th>
                  <th className="px-4 py-3">Sucursal</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200 bg-background-100">
                {filtered.map((invoice) => (
                  <tr key={invoice.id} className="transition-colors hover:bg-background-200/50">
                    <td className="px-4 py-3 text-sm font-semibold text-foreground-950">
                      {invoice.number ?? "-"}
                      {invoice.ncf ? (
                        <span className="ml-2 text-[11px] font-normal text-foreground-400">{invoice.ncf}</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{formatDateTime(invoice.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      {invoice.customer_name ?? "Consumidor final"}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{invoice.cashier_name ?? "-"}</td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{invoice.branch_name ?? "-"}</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-foreground-950">
                      {formatMoney(invoice.total)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${statusStyle(invoice.status)}`}>
                        {statusLabel(invoice.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setSelectedId(invoice.id)}
                        className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        <i className="ri-eye-line text-base"></i>
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
            {filtered.map((invoice) => (
              <button
                key={invoice.id}
                type="button"
                onClick={() => setSelectedId(invoice.id)}
                className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 text-left transition-colors hover:bg-background-200/50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950">{invoice.number ?? "-"}</p>
                    <p className="mt-0.5 text-[11px] text-foreground-500">{formatDateTime(invoice.created_at)}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${statusStyle(invoice.status)}`}>
                    {statusLabel(invoice.status)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate text-xs text-foreground-600">
                    {invoice.customer_name ?? "Consumidor final"}
                  </span>
                  <span className="font-heading text-base font-bold text-primary-600">
                    {formatMoney(invoice.total)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <InvoiceDetailModal
        invoiceId={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}